import { Injectable } from '@angular/core'
import {
    HttpClient,
    HttpErrorResponse,
    HttpHeaders,
} from '@angular/common/http'
import { BehaviorSubject, Observable, tap } from 'rxjs'
import { QueryGet } from '../../shared/query-types'
import { Pagination } from '../../models/pagination.interface'
import { Incident, IncidentDTO } from '../../models/incident.interface'
import { RequestService } from '../request/request.service'
import { environment } from '../../../environments/environment'
import { AsyncFormValidation } from '../../interceptors/error.interceptor'
@Injectable({ providedIn: 'root' })
export class IncidentService {
    httpOptions = {
        headers: new HttpHeaders({}),
    }

    private apiUrl = environment.service_1 + 'incidents/'
    private url = environment.service_1 + 'create-incident'
    private urlEdit = environment.service_1 + 'update-incident/'
    private urldelet = environment.service_1 + 'delete-incident/'
    private exportUrl = environment.service_1 + 'export-incident/'
    private encryptUrl = environment.service_1 + 'encrypt-incident/'
    private otpUrl = environment.service_1 + 'auth_user/otp/'
    private urlPath = environment.service_1 + 'export-madeja/'

    private incidents = new BehaviorSubject<Incident[]>([])
    incidents$ = this.incidents.asObservable()

    private asyncErrorValidation =
        new BehaviorSubject<AsyncFormValidation | null>(null)
    asyncErrorValidation$ = this.asyncErrorValidation.asObservable()

    private incidentSelected = new BehaviorSubject<Incident | null>(null)
    incidentSelected$ = this.incidentSelected.asObservable()

    constructor(
        private http: HttpClient,
        private requestService: RequestService
    ) {}

    getFacts(): Observable<any[]> {
        return this.http.get<any[]>(this.apiUrl)
    }

    getAll(): Observable<any[]> {
        return this.http.get<any[]>(`${this.apiUrl}`)
    }

    getById(id: string): Observable<Incident> {
        return this.http.get<Incident>(`${this.apiUrl}${id}/`)
    }

    createFacts(caso: any): Observable<any> {
        return this.http.post(this.apiUrl, caso)
    }

    update(id: string, data: any) {
        return this.http.put(`${this.apiUrl}/${id}/`, data)
    }

    setIncidents(incidents: Incident[]) {
        this.incidents.next(incidents)
    }

    setIncident(incident: Incident) {
        this.incidentSelected.next(incident)
    }

    createIncident(incident: IncidentDTO | FormData) {
        return this.http.post<Incident>(this.url, incident).pipe(
            tap({
                error: (err: HttpErrorResponse) => {
                    this.asyncErrorValidation.next(err.error)
                },
            })
        )
    }

    getIncidentId(id: number | string) {
        return this.http.get<Incident>(this.apiUrl + `${id}/`)
    }

    updateIncident(id: number, incident: Incident | FormData) {
        return this.http.put<Incident>(this.urlEdit + id, incident).pipe(
            tap({
                error: (err: HttpErrorResponse) =>
                    this.asyncErrorValidation.next(err.error),
            })
        )
    }

    allIncidents(queryParams: QueryGet): Observable<Pagination<Incident[]>> {
        return this.requestService.get<Incident[]>(this.apiUrl, queryParams)
    }

    deleteIncident(id: number): Observable<void> {
        return this.http.delete<void>(this.urldelet + id)
    }

    exportIncident(queryParams: QueryGet) {
        return this.requestService.getAxios(this.exportUrl, queryParams).pipe(
            tap({
                error: (err: HttpErrorResponse) => {
                    this.asyncErrorValidation.next(err.error)
                },
            })
        )
    }

    generateOtp(): Observable<any> {
        return this.http.get(this.otpUrl, {}).pipe(
            tap({
                error: (err: HttpErrorResponse) => {
                    this.asyncErrorValidation.next(err.error)
                },
            })
        )
    }

    encryptIncident(id: number, otp: string): Observable<any> {
        return this.http.post(`${this.encryptUrl}${id}`, { otp }).pipe(
            tap({
                error: (err: HttpErrorResponse) => {
                    this.asyncErrorValidation.next(err.error)
                },
            })
        )
    }

    downloadFile(): Observable<Blob> {
        const url = `${this.exportUrl}`
        return this.http.get(url, { responseType: 'blob' }).pipe(
            tap({
                error: (err: HttpErrorResponse) => {
                    this.asyncErrorValidation.next(err.error)
                },
            })
        )
    }

    getYears(futureYears: number): number[] {
        const currentYear = new Date().getFullYear()
        return Array.from(
            { length: futureYears + 3 },
            (_, i) => currentYear - 2 + i
        )
    }

    stripNullIds<T>(input: T): T {
        if (Array.isArray(input)) {
            return input
                .map((item) => this.stripNullIds(item))
                .filter(
                    (item) => item !== null && item !== undefined
                ) as unknown as T
        }
        if (input && typeof input === 'object') {
            const out: any = {}
            for (const [key, value] of Object.entries(
                input as Record<string, any>
            )) {
                if (key === 'id' && (value === null || value === undefined))
                    continue
                const cleaned = this.stripNullIds(value)
                if (
                    key === 'geoRef' &&
                    (!cleaned ||
                        (typeof cleaned === 'object' &&
                            !cleaned.latitude &&
                            !cleaned.longitude))
                ) {
                    continue
                }
                if (
                    typeof cleaned === 'object' &&
                    cleaned &&
                    Object.keys(cleaned).length === 0
                ) {
                    continue
                }
                if (cleaned !== null && cleaned !== undefined) {
                    out[key] = cleaned
                }
            }
            return out
        }
        return input
    }

    hasValidValues = (obj: any): boolean => {
        if (obj === null || obj === undefined) return false
        if (typeof obj === 'string') return obj.trim() !== ''
        if (typeof obj === 'number') return obj !== 0
        if (typeof obj === 'boolean') return obj === true
        if (Array.isArray(obj))
            return obj.some((item) => this.hasValidValues(item))
        if (typeof obj === 'object')
            return Object.values(obj).some((value) =>
                this.hasValidValues(value)
            )
        return false
    }

    formatBackendErrors(errors: any, prefix = ''): string {
        let messages: string[] = []
        for (const key of Object.keys(errors)) {
            const value = errors[key]
            const label = prefix ? `${prefix} > ${key}` : key
            if (Array.isArray(value)) {
                value.forEach((msg) => messages.push(`${label}: ${msg}`))
            } else if (typeof value === 'object' && value !== null) {
                messages.push(this.formatBackendErrors(value, label))
            }
        }
        return messages.join('\n')
    }
    exportMadeja(queryParams: QueryGet) {
        return this.requestService.getAxios(this.urlPath, queryParams).pipe(
            tap({
                error: (err: HttpErrorResponse) => {
                    this.asyncErrorValidation.next(err.error)
                },
            })
        )
    }

    calculateAge(dateBirth: Date): number | null {
        if (!dateBirth) return null

        const today = new Date()
        const birth = new Date(dateBirth)
        let age = today.getFullYear() - birth.getFullYear()
        const month = today.getMonth() - birth.getMonth()

        if (month < 0 || (month === 0 && today.getDate() < birth.getDate())) {
            age--
        }

        return age
    }

    setDateTimeToForm(dateString: string) {
        if (!dateString) return { date: null, time: '' }

        const date = new Date(dateString)

        const onlyDate = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate()
        )

        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')

        return {
            date: onlyDate,
            time: `${hours}:${minutes}`,
        }
    }
}
